import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import {
  FunctionComponent,
  memo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import { useChangeFlagDeviceMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing(0.25),
  },
  tooltip: {
    cursor: 'pointer',
  },
  select: {
    marginBottom: theme.spacing(4),
  },
}));

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

interface Flag {
  label: string;
  value: string;
}

const FlagDeviceDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const classes = useStyles();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const { enqueueSnackbar } = useSnackbar();

    const availableFlags = [
      {
        value: 'lost/stolen',
        label: 'Lost/Stolen',
      },
      {
        value: 'faulty/damaged',
        label: 'Faulty/Damaged',
      },
      {
        value: 'general/other',
        label: 'General/Other',
      },
    ];

    const [flagDevices, { isLoading }] = useChangeFlagDeviceMutation();
    const [flags, setFlags] = useState<string[]>([]);

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    useEffect(() => {
      if (
        isOpen &&
        devices.length > 0 &&
        devices[0].properties &&
        devices[0].properties.status
      ) {
        setFlags(devices[0].properties.status);
      }
    }, [devices, isOpen]);

    const findFlag = (flag: string): Flag | undefined =>
      availableFlags.find((f) => f.value === flag);

    return (
      <>
        <Dialog
          data-testid="change-target-system-dialog"
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Flag Device</DialogTitle>
          <DialogContent>
            <MedSelect
              name="flags"
              placeholder="Flags"
              multiple
              value={flags}
              options={availableFlags}
              onChange={(e) => {
                // @ts-ignore
                setFlags(e.target.value);
              }}
              MenuProps={{
                anchorOrigin: { vertical: -110, horizontal: 'left' },
                getContentAnchorEl: null,
              }}
              itemDisabled={(flag: Flag) => {
                if (flag.value === 'general/other') {
                  return !flags.includes('general/other') && flags.length > 0;
                }
                return flags.includes('general/other');
              }}
              valueGetter={(o) => o.value}
              optionTextGetter={(o) => o.label}
              renderValue={(selectedFlags) => {
                return (
                  <div className={classes.chips}>
                    {(selectedFlags as string[])
                      .map((flag) => findFlag(flag))
                      .map((flag) => {
                        return (
                          <Chip
                            label={flag?.label}
                            className={classes.chip}
                            key={flag?.value}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onDelete={(e) => {
                              setFlags(
                                flags.filter((f) => f !== flag?.value) || []
                              );
                            }}
                          />
                        );
                      })}
                  </div>
                );
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              onClick={() => {
                flagDevices({
                  apiBaseURL: config.apiBaseURL,
                  orgIDs:
                    authUser.selectedOrgs.length === 1
                      ? authUser.selectedOrgs
                      : authUser.orgs.map((o) => o.id),
                  devices,
                  flags,
                }).then((resp: any) => {
                  if (!resp.error) {
                    handleClose();
                    handleCreated();
                    return;
                  }
                  enqueueSnackbar(parseErrorMessage(resp.error), {
                    variant: 'error',
                  });
                });
              }}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default FlagDeviceDialog;
